import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";

import { getOptionValue } from "common/enums";
import BreadCrumb from "Components/Common/BreadCrumb";
import DashboardItem from "./DashboardItem";
import Backdrop from "Components/Common/Backdrop";

import { useBillingQuery } from "apis/Billing";
import { useGetUserBackupPowerCalculationsQuery } from "apis/BackupPower";
import { useGetUserSecurityDepositsCalculationsQuery } from "apis/SecurityDeposit";

import { DashboardBox, WorkInProgressText } from "./style";

const ConsumerDashboard = ({ t }) => {
  const {
    data: { data: billCalculations } = {},
    isLoading: isBillCalculationLoading,
  } = useBillingQuery();

  const {
    data: { data: securityDepositCalculations = [] } = {},
    isLoading: isSecurityDepositBillsLoading,
  } = useGetUserSecurityDepositsCalculationsQuery();

  const { data: { data: backupPowerData = [] } = {}, isLoading } =
    useGetUserBackupPowerCalculationsQuery();

  const {
    electricityBills,
    dcsBillCalculations,
    waterBillCalculations,
    sewageBillCalculations,
  } = useMemo(
    () =>
      billCalculations?.reduce(
        (acc, curr) => {
          if (curr.utilityId === getOptionValue("utilityOptions", "Power")) {
            acc.electricityBills.push(curr);
          } else if (
            curr.utilityId === getOptionValue("utilityOptions", "DCS")
          ) {
            acc.dcsBillCalculations.push(curr);
          } else if (
            curr.utilityId === getOptionValue("utilityOptions", "Water")
          ) {
            acc.waterBillCalculations.push(curr);
          } else if (
            curr.utilityId === getOptionValue("utilityOptions", "Sewage")
          ) {
            acc.sewageBillCalculations.push(curr);
          }
          return acc;
        },
        {
          electricityBills: [],
          dcsBillCalculations: [],
          waterBillCalculations: [],
          sewageBillCalculations: [],
        }
      ) ?? {
        electricityBills: [],
        dcsBillCalculations: [],
        waterBillCalculations: [],
        sewageBillCalculations: [],
      },
    [billCalculations]
  );

  if (isBillCalculationLoading || isSecurityDepositBillsLoading || isLoading) {
    return <Backdrop open />;
  }

  return (
    <>
      <BreadCrumb title={t("Dashboard")} />
      {billCalculations?.length ||
      securityDepositCalculations.length ||
      backupPowerData.length ? (
        <Grid container spacing={1}>
          {!!electricityBills.length && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5">
                  {t("Electricity Bills") + ":"}
                </Typography>
              </Grid>
              {electricityBills.map((item, index) => (
                <DashboardItem
                  item={item}
                  billType={getOptionValue("subscriptionTypes", "Electricity")}
                  key={index}
                />
              ))}
            </>
          )}
          {!!dcsBillCalculations.length && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5">{t("DCS Bills") + ":"}</Typography>
              </Grid>
              {dcsBillCalculations.map((item, index) => (
                <DashboardItem
                  item={item}
                  isOtherUtility
                  billType={getOptionValue("subscriptionTypes", "Electricity")}
                  key={index}
                />
              ))}
            </>
          )}
          {!!waterBillCalculations.length && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5">{t("Water Bills") + ":"}</Typography>
              </Grid>
              {waterBillCalculations.map((item, index) => (
                <DashboardItem
                  isOtherUtility
                  item={item}
                  billType={getOptionValue("subscriptionTypes", "Electricity")}
                  key={index}
                />
              ))}
            </>
          )}
          {!!sewageBillCalculations.length && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5">{t("Sewage Bills") + ":"}</Typography>
              </Grid>
              {sewageBillCalculations.map((item, index) => (
                <DashboardItem
                  isOtherUtility
                  item={item}
                  billType={getOptionValue("subscriptionTypes", "Electricity")}
                  key={index}
                />
              ))}
            </>
          )}
          {!!securityDepositCalculations.length && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5">
                  {t("Security Deposit Bills") + ":"}
                </Typography>
              </Grid>
              {securityDepositCalculations.map((item, index) => (
                <DashboardItem
                  item={item}
                  billType={getOptionValue(
                    "subscriptionTypes",
                    "Security Deposit"
                  )}
                  key={index}
                />
              ))}
            </>
          )}

          {!!backupPowerData.length && (
            <>
              <Grid item xs={12}>
                <Typography variant="h5">
                  {t("Backup Power Bills") + ":"}
                </Typography>
              </Grid>
              {backupPowerData.map((item, index) => (
                <DashboardItem
                  item={item}
                  billType={getOptionValue("subscriptionTypes", "Backup Power")}
                  key={index}
                />
              ))}
            </>
          )}
        </Grid>
      ) : (
        <DashboardBox>
          <WorkInProgressText>
            {t("There are no bills to pay")}.
          </WorkInProgressText>
        </DashboardBox>
      )}
    </>
  );
};

ConsumerDashboard.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(ConsumerDashboard);
